import { useCallback } from 'react';

type TextSegment = {
  text: string;
  x: number;
  y: number;
  width: number;
};

type TextPosition = {
  x: number;
  y: number;
};

type TextSegmentationOptions = {
  measureText: (text: string) => number;
  displayValue: string;
};

/**
 * Hook for text segmentation and positioning
 * Returns functions to calculate character positions and segment text for wrapping and highlighting
 */
export default function useTextSegmentation({
  measureText,
  displayValue,
}: TextSegmentationOptions) {
  /**
   * Calculate the exact position of a character in the textarea with pixel-perfect precision
   */
  const getCharacterPosition = useCallback((
    textBeforeChar: string,
    lineHeight: number,
    paddingLeft: number,
    paddingTop: number,
    textareaWidth: number,
  ): TextPosition => {
    if (!textBeforeChar) {
      return { x: paddingLeft, y: paddingTop };
    }

    const lines = textBeforeChar.split('\n');
    const lineIndex = lines.length - 1;
    const lastLine = lines[lineIndex] || '';

    // Calculate y position based on line number
    const y = paddingTop + (lineIndex * lineHeight);

    // We'll be more precise by measuring character-by-character
    let x = paddingLeft;
    let currentLineWidth = 0;
    let wrappedLines = 0;

    // Process each character to accurately determine position with wrapping
    for (let i = 0; i < lastLine.length; i++) {
      // Calculate width precisely - some characters need special handling
      const char = lastLine.charAt(i);
      const charWidth = measureText(char);

      if (currentLineWidth + charWidth > textareaWidth - (paddingLeft * 2)) {
        // Line wraps here
        wrappedLines++;
        currentLineWidth = charWidth;
      } else {
        currentLineWidth += charWidth;
      }
    }

    // Final position with precision
    x += currentLineWidth;
    const finalY = y + (wrappedLines * lineHeight);

    return { x, y: finalY };
  }, [measureText]);

  /**
   * Accurate text line wrapper that preserves word boundaries and handles sentence endings
   */
  const getTextSegments = useCallback((
    text: string,
    startIndex: number,
    textareaWidth: number,
    paddingLeft: number,
    lineHeight: number,
    paddingTop: number,
  ): TextSegment[] => {
    if (!text) {
      return [];
    }

    // Check if the text ends with sentence-ending punctuation
    const endsWithPunctuation = /[.!?]$/.test(text);

    const beforeText = displayValue.substring(0, startIndex);
    const startPosition = getCharacterPosition(
      beforeText,
      lineHeight,
      paddingLeft,
      paddingTop,
      textareaWidth,
    );

    const segments: TextSegment[] = [];
    let remainingText = text;
    let currentPosition = { ...startPosition };

    // Process remaining text to calculate line breaks and word wrapping
    while (remainingText.length > 0) {
      // Space left on current line
      const lineRemainingWidth = textareaWidth - currentPosition.x;

      // Check if text fits on current line
      if (measureText(remainingText) <= lineRemainingWidth) {
        // Calculate exact width for better precision
        const exactWidth = measureText(remainingText);
        const adjustment = endsWithPunctuation && remainingText.length > 1 ? 0.5 : 0;

        segments.push({
          text: remainingText,
          x: currentPosition.x,
          y: currentPosition.y,
          width: exactWidth + adjustment,
        });
        break;
      }

      // Find where text should wrap based on available width
      let fitIndex = 0;
      let measuredWidth = 0;

      // Measure character-by-character for precise wrapping
      for (let i = 0; i < remainingText.length; i++) {
        const charWidth = measureText(remainingText.substring(0, i + 1));
        if (charWidth > lineRemainingWidth) {
          fitIndex = i;
          measuredWidth = measureText(remainingText.substring(0, i));
          break;
        }
      }

      // If we can't fit even one character, move to next line
      if (fitIndex === 0) {
        currentPosition = {
          x: paddingLeft,
          y: currentPosition.y + lineHeight,
        };
        continue;
      }

      // Try to break at word boundary for better readability
      let breakIndex = fitIndex;
      for (let i = fitIndex; i > 0; i--) {
        if (remainingText[i] === ' ') {
          breakIndex = i;
          measuredWidth = measureText(remainingText.substring(0, i));
          break;
        }
      }

      // Only use word boundary if we're not sacrificing too much space
      if (breakIndex > 0 && fitIndex - breakIndex < 15) {
        fitIndex = breakIndex;
      }

      // Add segment for this line
      const lineText = remainingText.substring(0, fitIndex);
      segments.push({
        text: lineText,
        x: currentPosition.x,
        y: currentPosition.y,
        width: measuredWidth,
      });

      // Prepare for next line
      remainingText = remainingText.substring(fitIndex).trimStart();
      currentPosition = {
        x: paddingLeft,
        y: currentPosition.y + lineHeight,
      };
    }

    return segments;
  }, [displayValue, getCharacterPosition, measureText]);

  return {
    getCharacterPosition,
    getTextSegments,
  };
}
